import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import { PrismThemeProvider } from 'theme';
import type { SummaryRenameDialogProps } from './SummaryRenameDialog.props';
import {
  SummaryRenameDialogField,
  SummaryRenameDialogInput,
  SummaryRenameDialogLabel,
} from './SummaryRenameDialog.styles';
import { SUMMARY_RENAME_DIALOG_TEST_IDS } from './SummaryRenameDialog.testIds';

const SUMMARY_NAME_INPUT_ID = 'summary-rename-name';

const normalizeName = (name: string) => name.trim().toLowerCase();

const getNameHelperText = ({ isDuplicateName, name }: { isDuplicateName: boolean; name: string }) => {
  if (!name.trim()) {
    return 'Name is required.';
  }

  if (isDuplicateName) {
    return 'A summary with this name already exists.';
  }

  return undefined;
};

const SummaryRenameDialog = ({
  currentName,
  existingNames,
  isSaving = false,
  onClose,
  onConfirm,
  open,
}: SummaryRenameDialogProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [nextName, setNextName] = useState('');
  const trimmedName = nextName.trim();
  const normalizedExistingNames = useMemo(
    () => new Set(existingNames.map(normalizeName)),
    [existingNames]
  );
  const isDuplicateName = Boolean(trimmedName) && normalizedExistingNames.has(normalizeName(trimmedName));
  const helperText = getNameHelperText({ isDuplicateName, name: nextName });
  const canSave = Boolean(trimmedName && !helperText && !isSaving);

  const handleConfirm = useCallback(() => {
    if (!canSave) {
      return;
    }

    onConfirm(trimmedName);
  }, [canSave, onConfirm, trimmedName]);

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  // Reset the field from the selected tab each time the dialog opens; it stays mounted between
  // renames, so a discarded edit would otherwise survive into the next one.
  useEffect(() => {
    if (!open) {
      return;
    }

    setNextName(currentName);
  }, [currentName, open]);

  // Focus on the Dialog's own transition-complete hook rather than a setTimeout: the kebab menu
  // that opens this dialog is a Popover, and closing it restores focus to the kebab button. That
  // restore can land after a timeout-based focus and silently leave the field unfocused.
  // Selecting the text too, so typing replaces a generated name like "New Dashboard" outright.
  const handleEntered = useCallback(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // The Dialog family reads Prism-only tokens (palette.neutral, shape.radius). The summary
  // workspace is not inside a Prism provider, so wrap here rather than at the call site.
  return (
    <PrismThemeProvider>
      <Dialog
        data-testid={SUMMARY_RENAME_DIALOG_TEST_IDS.dialog}
        fullWidth
        maxWidth="xs"
        onClose={isSaving ? undefined : onClose}
        open={open}
        TransitionProps={{ onEntered: handleEntered }}>
        <DialogHeader>
          <DialogTitle>Rename Summary</DialogTitle>
          <DialogCloseButton
            aria-label="Close rename summary dialog"
            data-testid={SUMMARY_RENAME_DIALOG_TEST_IDS.closeButton}
            disabled={isSaving}
            onClick={onClose}
            size="small">
            <DialogCloseIcon />
          </DialogCloseButton>
        </DialogHeader>
        <DialogContent>
          <SummaryRenameDialogField>
            <SummaryRenameDialogLabel htmlFor={SUMMARY_NAME_INPUT_ID}>
              Name
            </SummaryRenameDialogLabel>
            <SummaryRenameDialogInput
              data-testid={SUMMARY_RENAME_DIALOG_TEST_IDS.nameInput}
              disabled={isSaving}
              error={Boolean(helperText)}
              helperText={helperText ?? ' '}
              id={SUMMARY_NAME_INPUT_ID}
              inputRef={inputRef}
              onChange={event => setNextName(event.target.value)}
              onKeyDown={handleNameKeyDown}
              placeholder="Enter summary name"
              value={nextName}
            />
          </SummaryRenameDialogField>
        </DialogContent>
        <DialogActionFooter
          actionButtonTestId={SUMMARY_RENAME_DIALOG_TEST_IDS.saveButton}
          actionLabel="Rename"
          cancelButtonTestId={SUMMARY_RENAME_DIALOG_TEST_IDS.cancelButton}
          isActionDisabled={!canSave}
          isActionLoading={isSaving}
          isCancelDisabled={isSaving}
          onAction={handleConfirm}
          onCancel={onClose}
        />
      </Dialog>
    </PrismThemeProvider>
  );
};

export default SummaryRenameDialog;
